import { useTheme } from '@mui/material';
import { Bar as RechartsBar, BarProps as RechartsBarProps } from 'recharts';

import { BarShape, BarShapeProps } from './BarShape';
import { BarProps } from './types';
import { getThemeColor } from '../../../../utils/theme';
import { ANIMATION_DURATION_MS } from '../../_constants';
import { mapPaletteColorToDataColorToken } from '../../../../utils/mapPaletteColorToDataColorToken';
import { useChartConfig } from '../../ChartConfigProvider';
import { useChartSelection } from '../../ChartSelectionProvider';
import { useChartElementTooltip } from '../_hooks/useChartElementTooltip';

export const Bar = <T, V = string | number>({
  dataKey,
  isAnimationActive,
  getBarPropsByPoint,
  getActiveBarPropsByPoint,
  onBarClick,
  fill = 'indigo.200',
  stroke,
  activeBar = true,
  testId = 'chart-bar',
  tooltipConfig,
  selectionKey,
  hide,
  ...rest
}: BarProps<T, V>) => {
  const theme = useTheme();
  const chartConfig = useChartConfig({ isAnimationActive });
  const selection = useChartSelection();
  const hiddenBySelection =
    selectionKey !== undefined
      ? selection !== undefined && !selection.isVisible(selectionKey)
      : false;

  const semanticFill = mapPaletteColorToDataColorToken(fill, fill);
  const themedFill = getThemeColor(theme, semanticFill);
  const themedStroke = getThemeColor(
    theme,
    stroke ? mapPaletteColorToDataColorToken(stroke, stroke) : stroke
  );

  const internalId = useChartElementTooltip<T>({
    tooltipConfig,
    indicatorVariant: fill === 'none' ? 'outlined' : 'filled',
    getStylesByPoint: getBarPropsByPoint,
  });

  return (
    <RechartsBar
      hide={hiddenBySelection || hide}
      dataKey={dataKey as RechartsBarProps<T, V>['dataKey']}
      unit={internalId}
      shape={
        <BarShape
          // This needs to be done to make recharts types happy while keeping
          // the BarShape component type-safe
          {...({} as BarShapeProps<T>)}
          onClick={onBarClick}
          getCustomProps={getBarPropsByPoint}
          testId={testId}
        />
      }
      activeBar={
        activeBar === true ? (
          <BarShape
            {...({} as BarShapeProps<T>)}
            style={{ filter: 'brightness(1.3)' }}
            onClick={onBarClick}
            getCustomProps={getActiveBarPropsByPoint}
            testId={testId}
          />
        ) : (
          activeBar
        )
      }
      {...rest}
      fill={themedFill}
      stroke={themedStroke}
      isAnimationActive={chartConfig.isAnimationActive}
      animationDuration={ANIMATION_DURATION_MS}
      data-testid={testId}
    />
  );
};
